import React, { useEffect, useState } from 'react';
import {
  useIsJCUser,
  useSubscriptionData,
  useUserDetails,
} from '@hooks/user.hook';
import moment from 'moment';
import { Button, LoadingScroll } from 'geniebook-ui/dist';
import { useStudentExamResult } from '@hooks/student.hook';
import ExamResultBanner from './ExamResultBanner';
import { ExamResultTableItemProps } from '@components/exam_result/list/ExamResultTableItem';
import ExamResultTable from '@components/exam_result/list/ExamResultTable';
import { useViewWidth } from '@utils/helpers/viewWidth';
import FilterResultOnWeb from './filter/FilterOnWeb';
import FilterResultOnMobileAndTablet from './filter/FilterOnMobileAndTablet';
import FilterPreprocessor from './filter/FilterOptionListPreprocessor';
import {
  activeSubscriptionSubjectsArrayAtom,
  selectedFilterOptionAtom,
  studentHasActiveSubscriptionAtom,
} from '@atom/examResultAtom';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { showExamResultSubmissionSuccessAtom } from '@components/exam_result/form/AddExamResultForm';
import { Snackbar } from 'geniebook-ui/dist';
import { USER_VN_STUDENT } from '@utils/constants/user.constants';
import Cookies from 'js-cookie';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

interface SubscriptionInterface {
  is_trial_plan?: boolean;
  expiry_date?: string;
}

const ExamResult = () => {
  const width = useViewWidth();
  const userDetailQuery = useUserDetails();
  const { isJC } = useIsJCUser({ subjectId: undefined, generalPage: true });
  const querySubscription = useSubscriptionData();
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const [showSuccessSubmitExamResult, setShowSuccessSubmitExamResult] = useAtom(
    showExamResultSubmissionSuccessAtom,
  );
  const [, setStudentHasActiveSubscription] = useAtom(
    studentHasActiveSubscriptionAtom,
  );
  const [, setActiveSubscriptionSubjects] = useAtom(
    activeSubscriptionSubjectsArrayAtom,
  );

  const [selectedOption] = useAtom(selectedFilterOptionAtom);

  // set as empty string for 'all' option
  const subjectFilter =
    selectedOption.subject.toLowerCase() === 'all'
      ? ''
      : selectedOption.subject;
  const examFilter =
    selectedOption.type.toLowerCase() === 'all' ? '' : selectedOption.type;
  const levelFilter =
    selectedOption.level.toLowerCase() === 'all' ? '' : selectedOption.level;

  const studentExamResultQuery = useStudentExamResult(8, page, {
    subject: subjectFilter,
    exam: examFilter,
    level: levelFilter,
  });
  const [examResultList, setExamResultList] = useState<
    ExamResultTableItemProps[]
  >([]);

  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (studentExamResultQuery.data?.data) {
      const studentExamResult = studentExamResultQuery.data.data.data;
      const ExamResultItems: ExamResultTableItemProps[] = [];
      if (Array.isArray(studentExamResult)) {
        return;
      }

      if (studentExamResult.exam_list.length < 1) {
        setExamResultList([]);
        return;
      }
      studentExamResult.exam_list.forEach(item => {
        ExamResultItems.push({
          id: item.id,
          subject: item.subject,
          subject_color: item.color,
          level: item.level,
          type: item.exam_name,
          grade_id: item.exam_grade_id,
          exam_date: moment(item.exam_date),
          approval: item.approved,
          rejection_reason: item.rejection_reason,
          image_files: item.image_files,
          marks:
            item.marks === '' || item.marks === null
              ? 0
              : parseInt(item.marks, 10),
          max_marks:
            item.max_marks === '' || item.max_marks === null
              ? 0
              : parseInt(item.max_marks, 10),
        });
      });
      setExamResultList(prevExamResultList => [
        ...prevExamResultList,
        ...ExamResultItems,
      ]);

      // Check if examResultList count is fewer than studentExamResult.count
      if (
        examResultList.length + ExamResultItems.length >=
        studentExamResult.count
      ) {
        setShowMore(true);
      } else {
        setShowMore(false);
      }
    }
  }, [studentExamResultQuery.data, page]);

  // will reset the state if there's changes in the filter
  useEffect(() => {
    setPage(0);
    setExamResultList([]);
  }, [subjectFilter, examFilter, levelFilter]);

  function handleShowMore() {
    setShowMore(true);
    setPage(prevPage => prevPage + 1);
  }

  function isTrial(subscription: SubscriptionInterface) {
    return !!subscription.is_trial_plan;
  }

  function isExpired(subscription: SubscriptionInterface) {
    if (!subscription.expiry_date) {
      return true;
    }
    const now = moment();
    const expiryDate = moment.parseZone(subscription.expiry_date + '+08:00'); // from SGT time

    return expiryDate.isBefore(now);
  }

  useEffect(() => {
    if (querySubscription.data?.data?.data) {
      const rawData = querySubscription.data?.data?.data;
      let activeSubscriptionCount = rawData.length;

      let subscribedSubjectDataArray = [];

      for (let i = 0; i < rawData.length; i++) {
        const subscription = rawData[i];

        if (isTrial(subscription) || isExpired(subscription)) {
          activeSubscriptionCount--;
        }

        if (!isTrial(subscription) && !isExpired(subscription)) {
          subscribedSubjectDataArray.push({
            value: subscription.subject_id,
            label: subscription.subject,
          });
        }

        if (activeSubscriptionCount === 0)
          setStudentHasActiveSubscription(false);
      }

      setActiveSubscriptionSubjects(subscribedSubjectDataArray);
    }
  }, [querySubscription.data?.data]);

  // redirect vn students to other page as this feature is not intended for them
  useEffect(() => {
    if (Cookies.get('is_vn_student') === USER_VN_STUDENT) navigate('/profile');
  }, []);

  return (
    <div className={'lg:h-[calc(100vh-700px)] h-[calc(100vh-300px)]'}>
      {showSuccessSubmitExamResult !== '' ? (
        <Snackbar
          message={showSuccessSubmitExamResult}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSuccessSubmitExamResult('');
          }}
          className={'fixed left-1/2 -translate-x-1/2 top-[60px]'}
          states={'celebratory'}
        />
      ) : null}
      <div
        className={`flex justify-between max-lg:hidden ${
          isJC === false ? 'mb-8' : ''
        }`}
      >
        <div className={'text-neutral-whiteboard text-lg font-semibold'}>
          Exam Results
        </div>
      </div>
      <div className={'max-lg:mb-4 lg:mb-4 md:max-lg:mt-4'}>
        <ExamResultBanner />
      </div>

      <section className="flex flex-row justify-between mb-3 items-center">
        {examResultList.length <= 0 &&
        examFilter === '' &&
        subjectFilter === '' &&
        levelFilter === '' ? null : (
          <>
            <FilterPreprocessor />
            {width >= breakpoint.lg ? (
              <FilterResultOnWeb />
            ) : (
              <FilterResultOnMobileAndTablet />
            )}
          </>
        )}
        <Button
          variant="primary"
          onClick={() => {
            navigate('/exam-result/add');
          }}
          disabled={studentExamResultQuery.isLoading}
          className="w-32 h-10 ml-auto mr-2 text-md font-weight-800 whitespace-nowrap max-lg:hidden"
        >
          + Add
        </Button>
      </section>

      {studentExamResultQuery.isLoading || userDetailQuery.isLoading ? (
        <LoadingScroll isBlockOthers={true} />
      ) : (
        <ExamResultTable
          ExamResultList={examResultList}
          showMore={showMore}
          onShowMore={handleShowMore}
        />
      )}

      <div className="fixed bottom-[80px] left-0 right-0 flex justify-center items-center gap-4 font-bold text-sm lg:!hidden">
        <Button
          variant="primary"
          onClick={() => {
            navigate('/exam-result/add');
          }}
          disabled={studentExamResultQuery.isLoading}
          className="text-md w-[70vh] font-weight-800 whitespace-nowrap"
        >
          + Add New
        </Button>
      </div>
      {studentExamResultQuery.isLoading || userDetailQuery.isLoading ? (
        <LoadingScroll isBlockOthers={true} />
      ) : null}
    </div>
  );
};

export default ExamResult;
